<template>
	<div
		v-if="loading"
		class="loading-spinner-container"
	>
		<LoadingSpinner :is-overlay="true" />
	</div>
	<div v-else>
		<div class="journeys-skew-banner">
			<div class="container">
				<div class="row">
					<div class="col journeys-skew-banner__content text-center">
						<h1 class="h2">{{ microcopy?.heading }}</h1>
						<p>{{ microcopy?.summary }}</p>
					</div>
				</div>
			</div>
		</div>
		<div class="container pull-up">
			<form
				class="w-100"
				@submit.prevent
			>
				<div class="rounded-container rounded-container--single-item rounded-container--narrow">
					<div class="row">
						<div class="col">
							<TextInputField
								required
								:force-error-text="showError"
								:error-text="postcodeErrorLabel()"
								:prefill-input="inputValue"
								input-type="postcode"
								@field-input="(payload) => {
									this.showError = false;
									this.inputValue = payload.value;
									this.isValid = payload.isValid;
									this.postPostcode(this.inputValue);
								}"
							/>
						</div>
					</div>
				</div>
				<div class="row btm-btn-group m-auto mb-5">
					<div class="col text-center d-flex flex-column align-items-center">
						<GenericButton
							:label-text="microcopy?.nextButtonLabel"
							:button-disabled="!isValid"
							button-type="button"
							@button-click="handleClick"
						/>
						<BackButton
							:back-text="microcopy?.backButtonLabel"
							@back-click="handleBackButton"
							style="margin-top: 32px;"
						/>
					</div>
				</div>
			</form>
		</div>
	</div>
</template>
<script>
import TextInputField from '@/shared/components/TextInputField.vue';
import GenericButton from '@/shared/components/GenericButton.vue';
import BackButton from '@/shared/components/BackButton.vue';
import LoadingSpinner from '@/shared/components/LoadingSpinner.vue';

import router from '../router/index.js';

import { mapStores, mapState, mapActions } from 'pinia';
import { useAppointmentReferralStore } from '../stores/appointmentReferralStore.js';

import { createTimeoutSignal } from '@/shared/helpers';

export default {
	components: {
		TextInputField,
		GenericButton,
		BackButton,
		LoadingSpinner
	},
	data() {
		return {
			inputValue: '',
			isValid: false,
			lookupError: null,
			microcopy: '',
			loading: false,
			showError: false
		};
	},
	computed: {
		...mapStores(useAppointmentReferralStore),
		...mapState(useAppointmentReferralStore, ['postcode', 'profession', 'customerDetails', 'selectedAdviser']),
		homeOrWorkPostcode() {
			return ['Dentist', 'Teacher'].includes(this.profession.type) ? 'work' : 'home';
		},
		nextPage() {
			return this.profession.type === 'Teacher' ? 'before-you-book' : 'your-financial-adviser';
		},
	},
	methods: {
		...mapActions(useAppointmentReferralStore, ['postPostcode', 'postFinancialAdviser', 'postSelectedAdviser']),
		handleClick() {
			this.sendPostcodeToAPI();
		},
		postcodeErrorLabel() {
			return this.lookupError
				?? this.microcopy?.inputErrorMessage;
		},
		sendPostcodeToAPI() {
			this.loading = true;
			this.showError = false;

			this.postSelectedAdviser('');

			const myHeaders = new Headers();
			myHeaders.append("Content-Type", "application/json");
			const raw = JSON.stringify({
				"segment": this.profession.type,
				'professionLabel': this.profession.text,
				"postcode": this.inputValue,
			});

			appInsights.trackTrace({
				message: '[BAARefJourney] API new customer lookup - Started',
				properties: { area: 'appointment-referral-journey', hostname: location.hostname },
				severityLevel: 1
			});

			const options = {
				method: 'POST',
				headers: myHeaders,
				body: raw,
				redirect: 'follow',
				signal: createTimeoutSignal()
			};

			fetch('/sapi/FinancialConsultants/newCustomerSearch', options)
				.then(async (res) => {
					if (!res.ok) {
						console.error(`Request failed with status ${res.status}`);
						appInsights.trackTrace({
							message: `[BAARefJourney] API new customer lookup - Failed - received HTTP ${res.status}`,
							properties: { area: 'appointment-referral-journey', hostname: location.hostname },
							severityLevel: 4
						});

						if (res.status === 404) {
							this.lookupError = 'Please enter a valid UK postcode';
							return;
						}

						if (res.status === 400) {
							const data = await res.json();
							this.lookupError = Object.values(data.errors)[0] ?? 'Bad request in adviser lookup';
							return;
						}

						return router.push('form-unavailable');
					}

					if (res.status === 204) {
						appInsights.trackTrace({
							message: '[BAARefJourney] API new customer lookup - Succeeded - received HTTP 204',
							properties: { area: 'appointment-referral-journey', hostname: location.hostname },
							severityLevel: 1
						});

						this.lookupError = 'No financial adviser found';
						return;
					}

					appInsights.trackTrace({
						message: '[BAARefJourney] API new customer lookup - Succeeded',
						properties: { area: 'appointment-referral-journey', hostname: location.hostname },
						severityLevel: 1
					});

					this.lookupError = null;

					const data = await res.text();
					this.postFinancialAdviser(data);

					return router.push(this.nextPage);
				})
				.catch((e) => {
					if (
						e instanceof DOMException
						&& e.name === 'AbortError'
					) {
						console.error('Request timed out');
						appInsights.trackTrace({
							message: '[BAARefJourney] API new customer lookup - Failed - Timed out',
							properties: { area: 'appointment-referral-journey', hostname: location.hostname },
							severityLevel: 3
						});

						this.lookupError = 'Request timed out, please try again';
						return;
					}

					console.error(e);
					appInsights.trackException({
						exception: e,
						properties: { area: 'appointment-referral-journey' },
						severityLevel: 4
					});
					return router.push('form-unavailable');
				})
				.finally(() => {
					this.loading = false;
					this.showError = !!this.lookupError;
				});
		},
		handleBackButton() {
			router.back();
		},
	},
	mounted() {
		this.microcopy = this.homeOrWorkPostcode === 'home' ? window.bookAnAppointmentReferralMicrocopy?.homePostcode : window.bookAnAppointmentReferralMicrocopy?.workPostcode;
		document.title = this.microcopy?.metaTitle
	}
};
</script>
